import LazyMap from "@/components/site/LazyMap";
import PageHero from "@/components/site/PageHero";
import ReviewedByDrSimon from "@/components/site/ReviewedByDrSimon";
import HeroTrustBar from "@/components/site/HeroTrustBar";
import {
  Calendar,
  Clock,
  Brain,
  Network,
  ArrowRight,
  Lock,
  Check,
  Phone,
  AlertTriangle,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Header from "@/components/site/Header";
import SEOHead from "@/components/SEOHead";
import Footer from "@/components/site/Footer";
import FloatingCTAs from "@/components/site/FloatingCTAs";
import drSimonAsset from "@/assets/team/dr-jeffrey-simon.webp.asset.json";
import InsuranceCTABlock from "@/components/site/InsuranceCTABlock";
import InsurancePills from "@/components/site/InsurancePills";
import RelatedLinks from "@/components/site/RelatedLinks";
import { ServiceAreaPill, SERVICE_AREA_LINKS } from "@/components/site/ServiceAreaPills";

const PHONE = "856-200-3127";
const TEL = "tel:8562003127";
const PAGE_URL =
  "https://cherryhillrecoverycenter.com/synthetic-drug-treatment-nj";

const linkA: React.CSSProperties = { color: "#8B1A1A", textDecoration: "underline" };

const faqs = [
  {
    q: "What counts as a synthetic drug?",
    a: "Synthetic drugs are made in a lab to copy the effects of cannabis, stimulants, or opioids. The main groups are synthetic cannabinoids (K2, Spice), synthetic cathinones (bath salts, flakka), synthetic opioids (fentanyl analogs, nitazenes), tianeptine (sold as Zaza or gas station heroin), and designer benzodiazepines sold online. Product names change often and the strength varies batch to batch.",
  },
  {
    q: "Do I need detox before starting treatment?",
    a: "Sometimes. If you have been using daily, or using a synthetic opioid or a designer benzodiazepine, supervised withdrawal is safer. Our clinical team assesses every patient, coordinates detox placement when it is needed, and picks you up in PHP or IOP right after. Call 856-200-3127 for a free assessment.",
  },
  {
    q: "Will a drug test show these substances?",
    a: "Often not. Many synthetic compounds do not appear on standard drug panels. That is why we build your treatment plan from your history and symptoms rather than a test result alone.",
  },
  {
    q: "Are synthetic drugs treated differently than other substances?",
    a: "The core treatment is the same: assessment, stabilization, therapy, and psychiatric care. What changes is the medical caution. Because product strength varies and many people are using more than one substance, we start with a careful evaluation and coordinate medical detox when it is needed.",
  },
  {
    q: "Is tianeptine (Zaza) really addictive?",
    a: "Yes. Tianeptine acts on opioid receptors and can cause severe dependence, opioid-style withdrawal, and escalating doses — even though it is sold over the counter in some smoke shops and gas stations. We treat tianeptine dependence in PHP and IOP and coordinate detox when it is clinically indicated.",
  },
  {
    q: "Can synthetic drugs cause psychosis?",
    a: "They can. Synthetic cannabinoids and synthetic cathinones are both associated with agitation, paranoia, and psychotic symptoms that can persist after use stops. Our psychiatric team evaluates for these symptoms at intake and treats them alongside the substance use.",
  },
  {
    q: "Does insurance cover synthetic drug addiction treatment?",
    a: "Most major insurance plans cover PHP and IOP for substance use disorder under the Mental Health Parity and Addiction Equity Act. Our admissions team verifies your specific coverage before treatment begins at no cost to you.",
  },
  {
    q: "How long does treatment take?",
    a: "PHP typically runs two to six weeks. IOP typically runs eight to twelve weeks. Many patients complete PHP and step down to IOP. Our clinical team reviews progress regularly and adjusts the plan to individual need rather than a fixed schedule.",
  },
];

const SyntheticDrugTreatmentNJ = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Synthetic Drug Addiction Treatment in NJ"
        description="Outpatient treatment for K2, bath salts, synthetic opioids and tianeptine in NJ. PHP, IOP & psychiatric care. Call 856-200-3127."
        canonical={PAGE_URL}
        schemaJson={[
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]}
      />
      <Header />
      <main className="pt-[120px]">
        {/* BREADCRUMB */}
        <nav aria-label="Breadcrumb" style={{ background: "#FAF4F0", padding: "12px 0" }}>
          <div className="container" style={{ fontSize: 13, color: "#2C2C2C" }}>
            <a href="/" style={{ color: "#8B1A1A" }}>Home</a>
            <span style={{ margin: "0 8px", color: "#888" }}>›</span>
            <span>Synthetic Drug Addiction</span>
          </div>
        </nav>

        {/* HERO */}
        <PageHero
          label="CONDITIONS WE TREAT"
          h1={<>Synthetic Drug Addiction Treatment in <span style={{ color: "#FAC8C8" }}>New Jersey</span></>}
          subheadline="Outpatient care for K2, bath salts, synthetic opioids, and tianeptine (Zaza) — with psychiatric oversight and detox coordination when it is needed."
        />
        <HeroTrustBar variant="condition" />
        <ReviewedByDrSimon />

        {/* SECTION 2 — INTRO CALLOUT BAR */}
        <section style={{ background: "#FAF4F0", borderTop: "3px solid #8B1A1A", borderBottom: "3px solid #8B1A1A", padding: "20px 0" }}>
          <div className="container" style={{ textAlign: "center" }}>
            <p style={{ color: "#8B1A1A", fontSize: 16, fontWeight: 700, margin: "0 0 6px" }}>
              Not sure what you have been taking? We can still help. Our admissions team is available right now.
            </p>
            <p style={{ margin: "0 0 4px" }}>
              <a href={TEL} style={{ color: "#6B1414", fontSize: 20, fontWeight: 700, textDecoration: "none" }}>
                Call Cherry Hill Recovery Center — {PHONE}
              </a>
            </p>
            <p style={{ color: "#2C2C2C", fontSize: 14, margin: 0 }}>Free. Confidential. No commitment.</p>
          </div>
        </section>

        {/* SECTION 3 — INTRO & PRIMARY CONTENT */}
        <section style={{ background: "#FFFFFF", padding: "64px 0" }}>
          <div className="container grid lg:grid-cols-[65fr_35fr] gap-10">
            <div>
              <h2 style={{ color: "#8B1A1A", fontFamily: "'Playfair Display', Georgia, serif", fontSize: 30, margin: "0 0 18px" }}>
                Synthetic Drug Use in New Jersey
              </h2>
              <p style={{ color: "#2C2C2C", fontSize: 17, lineHeight: 1.75, margin: "0 0 28px" }}>
                Synthetic drugs are made in a lab to copy the effects of cannabis, stimulants, or opioids. They are sold in smoke shops, gas stations, and online under names like K2, Spice, bath salts, flakka, and Zaza. The labels change often. The strength changes too. Two packets that look identical can hit very differently — which is why overdose, psychosis, and dependence can appear with almost no warning. Cherry Hill Recovery Center treats synthetic drug use in outpatient care in Cherry Hill, New Jersey, with psychiatric oversight and <a href="/detox-referral-coordination-nj" style={linkA}>detox coordination</a> when it is needed.
              </p>

              <h3 style={{ color: "#6B1414", fontSize: 22, margin: "28px 0 12px", fontWeight: 700 }}>What Counts as a Synthetic Drug</h3>
              <p style={{ color: "#2C2C2C", fontSize: 17, lineHeight: 1.75, margin: "0 0 12px" }}>
                The term covers several very different groups of substances:
              </p>
              <ul style={{ color: "#2C2C2C", fontSize: 17, lineHeight: 1.75, margin: "0 0 24px", paddingLeft: 22 }}>
                <li style={{ marginBottom: 10 }}><strong>Synthetic cannabinoids (K2, Spice).</strong> Plant material or liquid sprayed with lab-made chemicals. Effects are far stronger and less predictable than cannabis, and can include severe agitation, vomiting, and psychosis.</li>
                <li style={{ marginBottom: 10 }}><strong>Synthetic cathinones (bath salts, flakka).</strong> Stimulants that can cause days of sleeplessness, paranoia, dangerous heart rate spikes, and overheating.</li>
                <li style={{ marginBottom: 10 }}><strong>Synthetic opioids (fentanyl analogs, nitazenes).</strong> Extremely potent opioids often mixed into pills or powder without the buyer knowing. Overdose risk is very high. See our <a href="/fentanyl-addiction-treatment-nj" style={linkA}>fentanyl treatment page</a>.</li>
                <li style={{ marginBottom: 10 }}><strong>Tianeptine (Zaza, gas station heroin).</strong> Sold over the counter in some shops. It acts on opioid receptors and can cause severe dependence and withdrawal. Read <a href="/what-is-zaza-drug-nj" style={linkA}>what Zaza actually is</a>.</li>
                <li><strong>Research chemicals and designer benzos.</strong> Sold online with unclear labeling. Dose strength varies batch to batch, which makes tapering on your own unsafe.</li>
              </ul>

              <h3 style={{ color: "#6B1414", fontSize: 22, margin: "28px 0 12px", fontWeight: 700 }}>Signs It Has Become a Problem</h3>
              <p style={{ color: "#2C2C2C", fontSize: 17, lineHeight: 1.75, margin: 0 }}>
                Common signs include buying products from smoke shops, gas stations, or online sellers to get high or to steady out; needing more of the same product to feel the effect; feeling sick, anxious, or sweaty when a dose is late; sudden mood swings, paranoia, or confusion after use; emergency room visits, seizures, or blackouts; and trying to quit and finding the withdrawal too hard to get through alone.
              </p>
            </div>

            {/* Sticky sidebar */}
            <aside style={{ position: "sticky", top: 90, alignSelf: "start", background: "#FAF4F0", border: "1px solid #F0E0E0", borderTop: "4px solid #8B1A1A", borderRadius: 12, padding: 28 }}>
              <h3 style={{ color: "#8B1A1A", fontSize: 18, fontWeight: 700, margin: "0 0 10px" }}>Get Help Today</h3>
              <p style={{ color: "#2C2C2C", fontSize: 14, lineHeight: 1.6, margin: "0 0 16px" }}>
                Free confidential assessment available now. Most insurance accepted. Same-day response.
              </p>
              <a href={TEL} aria-label="Call Cherry Hill Recovery Center" style={{ display: "block", textAlign: "center", background: "#8B1A1A", color: "#FAF4F0", padding: "12px 16px", borderRadius: 999, fontWeight: 700, textDecoration: "none" }}>
                Call {PHONE}
              </a>
              <hr style={{ border: 0, borderTop: "1px solid #F0E0E0", margin: "16px 0" }} />
              <a href="/verify-insurance" aria-label="Verify my insurance" style={{ display: "block", textAlign: "center", border: "2px solid #8B1A1A", color: "#8B1A1A", padding: "10px 16px", borderRadius: 999, fontWeight: 700, textDecoration: "none" }}>
                Verify My Insurance
              </a>
              <hr style={{ border: 0, borderTop: "1px solid #F0E0E0", margin: "16px 0" }} />
              <p style={{ color: "#2C2C2C", fontSize: 14, fontWeight: 700, margin: "0 0 8px" }}>Related Programs:</p>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 6 }}>
                <li><a href="/php-cherry-hill-nj" style={{ color: "#8B1A1A", fontSize: 14, textDecoration: "none" }}>→ Partial Hospitalization (PHP)</a></li>
                <li><a href="/iop-cherry-hill-nj" style={{ color: "#8B1A1A", fontSize: 14, textDecoration: "none" }}>→ Intensive Outpatient (IOP)</a></li>
                <li><a href="/evening-iop-new-jersey" style={{ color: "#8B1A1A", fontSize: 14, textDecoration: "none" }}>→ Evening IOP</a></li>
                <li><a href="/psychiatric-evaluation-nj" style={{ color: "#8B1A1A", fontSize: 14, textDecoration: "none" }}>→ Psychiatric Evaluations</a></li>
                <li><a href="/detox-referral-coordination-nj" style={{ color: "#8B1A1A", fontSize: 14, textDecoration: "none" }}>→ Detox Assessment & Referral</a></li>
              </ul>
              <hr style={{ border: 0, borderTop: "1px solid #F0E0E0", margin: "16px 0" }} />
              <div role="alert" style={{ background: "#FFFFFF", border: "1px solid #F0E0E0", borderLeft: "3px solid #8B1A1A", borderRadius: 8, padding: 16 }}>
                <p style={{ color: "#8B1A1A", fontSize: 13, fontWeight: 700, margin: "0 0 6px" }}>Unknown Potency Risk</p>
                <p style={{ color: "#2C2C2C", fontSize: 12, lineHeight: 1.55, margin: 0 }}>
                  Synthetic opioids can be present in pills and powder without any warning. Keep Narcan available — it is sold without a prescription at most NJ pharmacies.
                </p>
              </div>
              <hr style={{ border: 0, borderTop: "1px solid #F0E0E0", margin: "16px 0" }} />
              <p style={{ color: "#888", fontSize: 13, margin: 0 }}>
                1930 Marlton Pike East, Cherry Hill, NJ 08003<br />
                <a href="https://maps.google.com/?q=1930+Marlton+Pike+East+Cherry+Hill+NJ" target="_blank" rel="noopener noreferrer" style={{ color: "#8B1A1A" }}>Get Directions →</a>
              </p>
            </aside>
          </div>
        </section>

        {/* SECTION 4 — WHY SYNTHETICS ARE DIFFERENT */}
        <section style={{ background: "#FAF4F0", padding: "64px 0" }}>
          <div className="container" style={{ maxWidth: 1100 }}>
            <h2 style={{ color: "#8B1A1A", fontFamily: "'Playfair Display', Georgia, serif", fontSize: 30, textAlign: "center", margin: "0 0 14px" }}>
              Why Synthetic Drugs Need a Different Level of Caution
            </h2>
            <p style={{ color: "#2C2C2C", fontSize: 17, textAlign: "center", maxWidth: 820, margin: "0 auto 36px", lineHeight: 1.65 }}>
              The treatment model is familiar. The medical caution is not. Here is what changes when the substance is lab-made and unlabeled.
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                { t: "Potency Is Unpredictable", b: "There is no standard dose. Two packets from the same shelf can differ by many times in strength. That is why we do not assume a stable tolerance and why stopping abruptly on your own can be riskier than it seems." },
                { t: "Standard Drug Panels Miss Them", b: "Most synthetic compounds do not show on routine testing. We build the clinical picture from your history, your symptoms, and the products you have been buying — not from a lab result alone." },
                { t: "Psychiatric Symptoms Are Common", b: "Agitation, paranoia, panic, and psychosis appear more often with synthetic cannabinoids and cathinones than with the drugs they imitate. Psychiatric care is part of treatment from day one, not an add-on." },
              ].map((c) => (
                <div key={c.t} style={{ background: "#FFFFFF", borderRadius: 12, padding: 28, borderTop: "4px solid #8B1A1A", boxShadow: "0 6px 20px rgba(107,20,20,0.08)" }}>
                  <h3 style={{ color: "#8B1A1A", fontSize: 18, fontWeight: 700, margin: "0 0 12px" }}>{c.t}</h3>
                  <p style={{ color: "#2C2C2C", fontSize: 15, lineHeight: 1.7, margin: 0 }}>{c.b}</p>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 28, background: "#FFFFFF", borderLeft: "4px solid #8B1A1A", padding: "20px 24px", borderRadius: 8 }}>
              <p style={{ color: "#8B1A1A", fontSize: 16, fontWeight: 700, margin: "0 0 6px" }}>What About Products Sold Legally?</p>
              <p style={{ color: "#2C2C2C", fontSize: 15, lineHeight: 1.7, margin: 0 }}>
                Being sold at a store counter does not make a product safe or non-addictive. Tianeptine and several designer compounds are widely available and still produce real dependence and real withdrawal. Call <a href={TEL} style={linkA}>{PHONE}</a> for a free assessment.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 5 — TREATMENT OPTIONS */}
        <section style={{ background: "#FFFFFF", padding: "64px 0" }}>
          <div className="container" style={{ maxWidth: 1100 }}>
            <h2 style={{ color: "#8B1A1A", fontFamily: "'Playfair Display', Georgia, serif", fontSize: 32, textAlign: "center", margin: "0 0 14px" }}>
              Synthetic Drug Treatment Programs at Cherry Hill Recovery Center
            </h2>
            <p style={{ color: "#2C2C2C", fontSize: 18, textAlign: "center", maxWidth: 860, margin: "0 auto 36px", lineHeight: 1.65 }}>
              Assessment, stabilization, therapy, and psychiatric care — delivered by our board-certified medical and clinical team in Cherry Hill.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                { Icon: Network, t: "Clinical Assessment", b: "We start with a psychiatric and substance use evaluation. Because standard drug panels often miss synthetic compounds, your history and symptoms guide the plan — including whether supervised withdrawal is needed before treatment begins.", l: "Learn About Evaluations →", h: "/psychiatric-evaluation-nj" },
                { Icon: Calendar, t: "Partial Hospitalization Program (PHP)", b: "PHP runs five to six days a week with daily psychiatric oversight. It fits people coming off heavy use or dealing with paranoia, panic, or depression at the same time as withdrawal.", l: "Learn About PHP →", h: "/php-cherry-hill-nj" },
                { Icon: Clock, t: "Intensive Outpatient Program (IOP)", b: "IOP meets three to four days a week so you can keep working. Evening and virtual scheduling options are available across New Jersey for people who cannot step away from work or family.", l: "Learn About IOP →", h: "/iop-cherry-hill-nj" },
                { Icon: Brain, t: "Detox Referral & Coordination", b: "If you have been using synthetic opioids or designer benzodiazepines daily, talk to us before you stop cold. We arrange supervised detox placement and start treatment immediately afterward.", l: "Learn About Detox Referral →", h: "/detox-referral-coordination-nj" },
              ].map(({ Icon, t, b, l, h }) => (
                <div key={t} style={{ background: "#FAF4F0", borderRadius: 12, padding: 28, borderTop: "4px solid #8B1A1A", boxShadow: "0 6px 20px rgba(107,20,20,0.08)" }}>
                  <Icon size={28} color="#8B1A1A" />
                  <h3 style={{ color: "#8B1A1A", fontSize: 20, fontWeight: 700, margin: "12px 0 10px" }}>{t}</h3>
                  <p style={{ color: "#2C2C2C", fontSize: 15, lineHeight: 1.7, margin: "0 0 14px" }}>{b}</p>
                  <a href={h} style={{ color: "#8B1A1A", fontWeight: 700, textDecoration: "none" }}>{l}</a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 6 — THERAPIES */}
        <section style={{ background: "#FAF4F0", padding: "64px 0" }}>
          <div className="container" style={{ maxWidth: 1100 }}>
            <h2 style={{ color: "#8B1A1A", fontFamily: "'Playfair Display', Georgia, serif", fontSize: 32, margin: "0 0 14px" }}>
              Evidence-Based Therapies We Use
            </h2>
            <p style={{ color: "#2C2C2C", fontSize: 17, lineHeight: 1.7, margin: "0 0 32px", maxWidth: 900 }}>
              There is no medication that specifically reverses synthetic cannabinoid or cathinone use, so behavioral therapy and psychiatric care carry the weight. Where a synthetic opioid is involved, medication-assisted treatment is often part of the plan.
            </p>

            <div className="grid md:grid-cols-2 gap-5">
              {[
                { t: "Cognitive Behavioral Therapy (CBT)", b: "CBT helps you identify the triggers, routines, and thinking patterns that lead back to the store counter or the online order — and build specific responses that work in your real week." },
                { t: "Medication-Assisted Treatment (When Indicated)", b: "For tianeptine and synthetic opioid dependence, buprenorphine-based treatment is often appropriate. Our medical team evaluates this individually and manages it as part of your program." },
                { t: "Trauma-Informed and Dual-Diagnosis Care", b: "Anxiety, depression, PTSD, and psychosis are common alongside synthetic drug use. We treat both together, because untreated psychiatric symptoms are one of the strongest predictors of relapse." },
                { t: "Relapse Prevention and Craving Management", b: "Access is the hard part with synthetics — the product is often a short drive away. We work on environment change, craving skills, and a concrete plan for the moments that put you at risk." },
              ].map((c) => (
                <div key={c.t} style={{ background: "#FFFFFF", borderRadius: 12, borderTop: "4px solid #8B1A1A", padding: 24 }}>
                  <h3 style={{ color: "#8B1A1A", fontSize: 18, fontWeight: 700, margin: "0 0 10px" }}>{c.t}</h3>
                  <p style={{ color: "#2C2C2C", fontSize: 15, lineHeight: 1.7, margin: 0 }}>{c.b}</p>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 28, display: "flex", flexWrap: "wrap", gap: 10 }}>
              {["DBT", "Trauma-Informed Care", "Group Therapy", "Family Therapy", "Psychoeducation", "Mindfulness-Based Therapy", "Life Skills Training"].map((__n) => {
                const __href = (SERVICE_AREA_LINKS as Record<string, string>)[__n];
                return <ServiceAreaPill key={__n} name={__n} href={__href} />;
              })}
            </div>
          </div>
        </section>

        {/* SECTION 7 — WITHDRAWAL */}
        <section style={{ background: "#FFFFFF", padding: "64px 0" }}>
          <div className="container">
            <h2 style={{ color: "#8B1A1A", fontFamily: "'Playfair Display', Georgia, serif", fontSize: 32, margin: "0 0 14px" }}>
              Withdrawal — and Why Quitting Alone Is Hard
            </h2>
            <p style={{ color: "#2C2C2C", fontSize: 17, lineHeight: 1.7, margin: "0 0 32px", maxWidth: 900 }}>
              Many people expect no withdrawal from something sold on a store shelf. That is not how these substances work. Depending on what you used, stopping can bring nausea and stomach cramping, sweating, chills and body aches, racing heart and high blood pressure, insomnia, severe anxiety or depression, and strong cravings.
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                { p: "FIRST 24–72 HOURS", t: "Acute Symptoms", b: "Physical symptoms usually peak here — sweating, nausea, agitation, and disrupted sleep. For synthetic opioid or designer benzodiazepine dependence, this is the window where medical supervision matters most." },
                { p: "WEEK 1–2", t: "Mood and Sleep Instability", b: "Anxiety, irritability, low mood, and poor sleep tend to outlast the physical symptoms. Daily structure through PHP, along with psychiatric monitoring, carries most people through this stretch." },
                { p: "WEEKS TO MONTHS", t: "Cravings and Recovery", b: "Cravings return in waves, often triggered by driving past the place you bought from. Continued IOP and a written relapse prevention plan give you something to use in that moment." },
              ].map((c) => (
                <div key={c.p} tabIndex={0} style={{ background: "#FAF4F0", borderRadius: 12, padding: 28, borderTop: "4px solid #8B1A1A", boxShadow: "0 6px 20px rgba(107,20,20,0.08)", outline: "none" }}>
                  <span style={{ display: "inline-block", background: "#FFFFFF", color: "#8B1A1A", padding: "4px 12px", borderRadius: 50, fontSize: 11, fontWeight: 700, letterSpacing: "0.05em" }}>{c.p}</span>
                  <h3 style={{ color: "#8B1A1A", fontSize: 18, fontWeight: 700, margin: "12px 0 10px" }}>{c.t}</h3>
                  <p style={{ color: "#2C2C2C", fontSize: 15, lineHeight: 1.7, margin: 0 }}>{c.b}</p>
                </div>
              ))}
            </div>

            <div role="alert" style={{ marginTop: 28, background: "#FAF4F0", borderLeft: "4px solid #8B1A1A", padding: "20px 24px", borderRadius: 8 }}>
              <p style={{ color: "#8B1A1A", fontSize: 16, fontWeight: 700, margin: "0 0 6px", display: "flex", alignItems: "center", gap: 8 }}>
                <AlertTriangle size={18} /> Do Not Stop Synthetic Opioids or Designer Benzos Cold
              </p>
              <p style={{ color: "#2C2C2C", fontSize: 15, lineHeight: 1.7, margin: 0 }}>
                Abrupt withdrawal from these compounds can be medically serious, and returning to a previous dose after a break carries real overdose risk. Call <a href={TEL} style={linkA}>{PHONE}</a> and we will arrange supervised detox and start treatment right after.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 8 — FOR FAMILIES */}
        <section style={{ background: "#FAF4F0", padding: "64px 0" }}>
          <div className="container grid lg:grid-cols-[60fr_40fr] gap-10">
            <div>
              <h2 style={{ color: "#8B1A1A", fontFamily: "'Playfair Display', Georgia, serif", fontSize: 30, margin: "0 0 18px" }}>
                Helping a Loved One Using Synthetic Drugs
              </h2>
              <p style={{ color: "#2C2C2C", fontSize: 17, lineHeight: 1.75, margin: "0 0 18px" }}>
                Families often miss synthetic drug use because the products look harmless — a foil packet, a small bottle from a gas station, a shipment that arrives in the mail. What families usually notice first is behavior: extreme agitation or paranoia, sleeping for a day after being awake for two, or getting sick whenever a purchase is delayed.
              </p>
              <p style={{ color: "#2C2C2C", fontSize: 17, lineHeight: 1.75, margin: "0 0 18px" }}>
                Look for patterns rather than single incidents — repeated trips to the same shop, packaging you do not recognize, mood swings that track with those trips, and defensiveness about products they insist are legal.
              </p>
              <p style={{ color: "#2C2C2C", fontSize: 17, lineHeight: 1.75, margin: 0 }}>
                Cherry Hill Recovery Center treats the whole family. Family education, family therapy when appropriate, and our free <a href="/family-guide-new-jersey" style={linkA}>family guide</a> give you practical footing. <a href="/contact" style={linkA}>Contact our admissions team</a> to talk through your situation.
              </p>
            </div>

            <aside style={{ background: "#FFFFFF", borderTop: "4px solid #8B1A1A", borderRadius: 12, padding: 28 }}>
              <h3 style={{ color: "#8B1A1A", fontSize: 18, fontWeight: 700, margin: "0 0 14px" }}>For Family Members</h3>
              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 18px", display: "grid", gap: 10 }}>
                {[
                  "Legal to buy does not mean safe or non-addictive",
                  "Watch for repeat trips to the same shop or recurring online orders",
                  "Agitation, paranoia, or confusion after use is a medical warning sign",
                  "Keep Narcan available — synthetic opioids can be in anything",
                  "Call us before having the conversation — we will guide you through it",
                ].map((c) => (
                  <li key={c} style={{ display: "flex", gap: 10, alignItems: "flex-start", color: "#2C2C2C", fontSize: 14, lineHeight: 1.55 }}>
                    <Check size={16} color="#8B1A1A" style={{ flexShrink: 0, marginTop: 3 }} /> {c}
                  </li>
                ))}
              </ul>
              <a href="/family-guide-new-jersey" style={{ color: "#8B1A1A", fontWeight: 700, textDecoration: "none", display: "block", marginBottom: 10 }}>Download Family Guide →</a>
              <a href={TEL} style={{ color: "#8B1A1A", fontWeight: 700, fontSize: 18, textDecoration: "none" }}>Call {PHONE}</a>
            </aside>
          </div>
        </section>

        {/* SECTION 9 — CLINICAL TEAM */}
        <section style={{ background: "#FFFFFF", padding: "64px 0" }}>
          <div className="container">
            <h2 style={{ color: "#8B1A1A", fontFamily: "'Playfair Display', Georgia, serif", fontSize: 30, margin: "0 0 14px" }}>
              Our Medical and Clinical Team
            </h2>
            <p style={{ color: "#2C2C2C", fontSize: 17, lineHeight: 1.7, margin: "0 0 32px", maxWidth: 900 }}>
              Treatment at Cherry Hill Recovery Center is delivered by licensed therapists, addiction counselors, and medical professionals — overseen by our Chief Medical Officer, board-certified psychiatrist Dr. Jeffrey Simon. Every patient receives a psychiatric evaluation, an individualized plan, and ongoing clinical oversight through PHP or IOP.
            </p>

            <div className="grid md:grid-cols-[240px_1fr] gap-8 items-start">
              <div style={{ textAlign: "center" }}>
                <img
                  width={200}
                  height={200}
                  src={drSimonAsset.url}
                  alt="Dr. Jeffrey Simon, MD, Chief Medical Officer at Cherry Hill Recovery Center, Cherry Hill NJ"
                  loading="lazy"
                  style={{ width: 200, height: 200, borderRadius: "50%", objectFit: "cover", border: "4px solid #FAF4F0", boxShadow: "0 6px 20px rgba(107,20,20,0.15)" }}
                />
                <p style={{ color: "#888", fontSize: 14, fontStyle: "italic", margin: "12px 0 0" }}>
                  Dr. Jeffrey Simon, MD — Chief Medical Officer
                </p>
              </div>
              <div>
                <p style={{ color: "#2C2C2C", fontSize: 17, lineHeight: 1.75, margin: "0 0 18px" }}>
                  Dr. Simon is a board-certified psychiatrist specializing in addiction medicine and co-occurring mental health disorders. With synthetic drug use, his approach starts with caution about what is actually in the product and close attention to psychiatric symptoms — agitation, paranoia, and mood instability — that often need treatment alongside the substance use itself.
                </p>
                <a href="/about" style={{ color: "#8B1A1A", fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
                  Meet Our Full Team <ArrowRight size={16} />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 10 — INSURANCE */}
        <section style={{ background: "#FAF4F0", padding: "64px 0" }}>
          <div className="container" style={{ maxWidth: 1000 }}>
            <h2 style={{ color: "#8B1A1A", fontFamily: "'Playfair Display', Georgia, serif", fontSize: 32, margin: "0 0 16px" }}>
              Does Insurance Cover Synthetic Drug Treatment in New Jersey?
            </h2>
            <p style={{ color: "#2C2C2C", fontSize: 17, lineHeight: 1.7, margin: "0 0 28px" }}>
              Most major plans cover substance use disorder treatment — including PHP and IOP — under the Mental Health Parity and Addiction Equity Act. Our admissions team <a href="/verify-insurance" style={linkA}>verifies your coverage</a> before treatment begins at no cost to you, so you know your benefits before anything starts.
            </p>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 32 }}>
              <InsurancePills style={{ marginBottom: 32 }} />
            </div>

            <div style={{ textAlign: "center" }}>
              <a href="/verify-insurance" aria-label="Verify my insurance — takes 2 minutes" style={{ display: "inline-block", background: "#8B1A1A", color: "#FAF4F0", padding: "16px 40px", borderRadius: 999, fontWeight: 700, textDecoration: "none", fontSize: 16 }}>
                Verify My Insurance — Takes 2 Minutes
              </a>
            </div>
          </div>
        </section>

        {/* SECTION 11 — SERVICE AREA */}
        <section style={{ background: "#FFFFFF", padding: "64px 0" }}>
          <div className="container">
            <h2 style={{ color: "#8B1A1A", fontFamily: "'Playfair Display', Georgia, serif", fontSize: 30, margin: "0 0 14px" }}>
              Serving Cherry Hill & All of South Jersey
            </h2>
            <p style={{ color: "#2C2C2C", fontSize: 17, lineHeight: 1.7, margin: "0 0 24px", maxWidth: 900 }}>
              We serve patients from Camden County, Burlington County, Gloucester County, Atlantic County, and the greater South Jersey and Philadelphia region. <a href="/virtual-iop-new-jersey" style={linkA}>Virtual IOP</a> extends access anywhere in New Jersey. Our center is at 1930 Marlton Pike East in Cherry Hill, easily reached from Routes 70 and 38.
            </p>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 28 }}>
              {["Cherry Hill", "Camden", "Voorhees", "Marlton", "Moorestown", "Haddonfield", "Mount Laurel", "Medford", "Evesham", "Pennsauken", "Burlington County", "Gloucester County", "Atlantic County", "Philadelphia Area", "All of NJ (Virtual IOP)"].map((p) => (
                <span key={p} style={{ background: "#FAF4F0", border: "1px solid #F0E0E0", borderRadius: 50, padding: "8px 16px", color: "#8B1A1A", fontSize: 14, fontWeight: 600 }}>{p}</span>
              ))}
            </div>

            <div style={{ borderRadius: 12, overflow: "hidden", border: "1px solid #F0E0E0", maxWidth: 900, margin: "0 auto" }}>
              <LazyMap src="https://www.google.com/maps?q=1930+Marlton+Pike+East,+Cherry+Hill,+NJ+08003&output=embed" title="Cherry Hill Recovery Center location map" height={320} />
            </div>
          </div>
        </section>

        <InsuranceCTABlock />

        {/* SECTION 12 — FAQ */}
        <section style={{ background: "#FFFFFF", padding: "64px 0" }}>
          <div className="container" style={{ maxWidth: 900 }}>
            <h2 style={{ color: "#8B1A1A", fontFamily: "'Playfair Display', Georgia, serif", fontSize: 32, margin: "0 0 28px" }}>
              Frequently Asked Questions About Synthetic Drug Treatment in NJ
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} style={{ borderBottom: "1px solid #F0E0E0" }}>
                  <AccordionTrigger style={{ background: "#FFFFFF", color: "#2C2C2C", fontSize: 16, fontWeight: 600, padding: "18px 4px", textAlign: "left" }}>
                    <h3 style={{ margin: 0, color: "#2C2C2C", fontSize: 16, fontWeight: 600 }}>{f.q}</h3>
                  </AccordionTrigger>
                  <AccordionContent style={{ background: "#FAF4F0", color: "#2C2C2C", fontSize: 15, lineHeight: 1.7, padding: "16px 18px" }}>
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* SECTION 13 — BOTTOM CTA */}
        <section style={{ background: "linear-gradient(90deg, #6B1414 0%, #8B1A1A 100%)", padding: "64px 0" }}>
          <div className="container" style={{ textAlign: "center", maxWidth: 900 }}>
            <h2 style={{ color: "#FFFFFF", fontFamily: "'Playfair Display', Georgia, serif", fontSize: 36, margin: "0 0 14px" }}>
              You Do Not Need to Know What Was in It to Get Help.
            </h2>
            <p style={{ color: "#FAF4F0", fontSize: 18, lineHeight: 1.6, margin: "0 0 28px" }}>
              PHP, IOP, and psychiatric care delivered by our board-certified medical and clinical team. Free, confidential, most insurance accepted.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center", marginBottom: 22 }}>
              <a href={TEL} aria-label="Call Cherry Hill Recovery Center at 856-200-3127" style={{ background: "#FAF4F0", color: "#8B1A1A", padding: "14px 32px", borderRadius: 999, fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Phone size={18} /> Call {PHONE}
              </a>
              <a href="/contact" aria-label="Send us a message" style={{ border: "2px solid #FAF4F0", color: "#FAF4F0", padding: "12px 32px", borderRadius: 999, fontWeight: 700, textDecoration: "none" }}>
                Send Us a Message
              </a>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 24, justifyContent: "center", color: "#FAC8C8", fontSize: 14 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Lock size={14} /> 100% Confidential</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Check size={14} /> Insurance Accepted</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Phone size={14} /> Same-Day Response</span>
            </div>
          </div>
        </section>

        <RelatedLinks exclude={["/synthetic-drug-treatment-nj"]} />
      </main>
      <Footer />
      <FloatingCTAs />
    </div>
  );
};

export default SyntheticDrugTreatmentNJ;
